import React from 'react';
import { CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell } from '@coreui/react';
import { DownArrowIcon } from '../../assets/images';
import SimOFPTableItem from './SimOFPTableItem';
import { ISimFlightPlan, setSimFlightPlans } from '../../slice/simSlice';
import SimOFPTemplateTableItem from './SimOFPTemplateTableItem';

import Access from '../../components/Access';
import { AccessType } from '../../constants/roleAccessConstant';

import { useAppDispatch } from '../../app/hooks';

export type SortBy = 'std' | 'ufi' | 'aircraftType' | 'aircraftReg' | 'status';
export type SortOrder = 'asc' | 'desc';
export type SortMode = 'client' | 'server';

const getRowKey = (item: ISimFlightPlan, index: number) => {
  return String((item as any)?._id ?? item?.ufi ?? `${item?.std ?? ''}-${item?.aircraftReg ?? ''}-${index}`);
};

const isSameOrder = (left: ISimFlightPlan[], right: ISimFlightPlan[]) => {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    if (getRowKey(left[i], i) !== getRowKey(right[i], i)) return false;
  }
  return true;
};

const getPrimarySortValue = (item: ISimFlightPlan, key: SortBy) => {
  const value = (item as any)?.[key];
  const normalized = Array.isArray(value) ? (value?.[0] || '') : (value || '');
  return String(normalized);
};

const compareCreateTimeAsc = (a: ISimFlightPlan, b: ISimFlightPlan) => {
  const aTime = Date.parse(String(a?.createTime || ''));
  const bTime = Date.parse(String(b?.createTime || ''));
  const aValid = Number.isFinite(aTime);
  const bValid = Number.isFinite(bTime);

  if (aValid && bValid) return aTime - bTime;
  if (aValid) return -1;
  if (bValid) return 1;
  return 0;
};

const sortWithCreateTimeTieBreaker = (list: ISimFlightPlan[], key: SortBy, direction: SortOrder) => {
  return [...list].sort((a, b) => {
    const aKey = getPrimarySortValue(a, key);
    const bKey = getPrimarySortValue(b, key);
    const primaryResult = aKey.localeCompare(bKey, undefined, { numeric: true });

    if (primaryResult !== 0) {
      return direction === 'asc' ? primaryResult : -primaryResult;
    }

    // Tie-breaker is always createTime ascending: older first.
    return compareCreateTimeAsc(a, b);
  });
};

interface ISimOFPTableProps {
  simOFPs: ISimFlightPlan[];
  isSimTemplateConfiguration?: boolean;
  routePath: string;

  sortMode?: SortMode;

  onSort?: (key: SortBy) => void;

  // For arrow highlight / direction
  sortBy?: SortBy;
  sortOrder?: SortOrder;
  onMutateDone?: () => void;

  // Optional controlled client sort state (used by SimLandingPage)
  clientSortBy?: SortBy;
  clientSortOrder?: SortOrder;
  onClientSortChange?: (sortBy?: SortBy, sortOrder?: SortOrder) => void;
}

const SimOFPTable = ({
  simOFPs,
  isSimTemplateConfiguration = false,
  routePath,
  sortMode = 'client',
  onSort,
  sortBy,
  sortOrder,
  onMutateDone,
  clientSortBy,
  clientSortOrder,
  onClientSortChange,
}: ISimOFPTableProps) => {

  const dispatch = useAppDispatch();
  const [internalClientSortBy, setInternalClientSortBy] = React.useState<SortBy | undefined>(undefined);
  const [internalClientSortOrder, setInternalClientSortOrder] = React.useState<SortOrder | undefined>(undefined);

  const isClientSortControlled = sortMode === 'client' && typeof onClientSortChange === 'function';
  const resolvedClientSortBy = isClientSortControlled ? clientSortBy : internalClientSortBy;
  const resolvedClientSortOrder = isClientSortControlled ? clientSortOrder : internalClientSortOrder;

  const activeSortBy = sortMode === 'server' ? sortBy : resolvedClientSortBy;
  const activeSortOrder = sortMode === 'server' ? sortOrder : resolvedClientSortOrder;

  const dispatchSortAction = (key: SortBy, direction: SortOrder) => {
    const sortedList = sortWithCreateTimeTieBreaker([...(simOFPs || [])], key, direction);
    const next = sortedList.map((item: any) => ({ ...item }));
    if (isClientSortControlled) {
      onClientSortChange?.(key, direction);
    } else {
      setInternalClientSortBy(key);
      setInternalClientSortOrder(direction);
    }

    if (!isSimTemplateConfiguration) {
      dispatch(setSimFlightPlans(next));
    } else {
      return;
    }
  };

  const handleSortClick = (key: SortBy) => {
    if (sortMode === 'server') {
      onSort?.(key);
      return;
    }
    const nextSortOrder: SortOrder =
      resolvedClientSortBy !== key ? 'asc' : resolvedClientSortOrder === 'asc' ? 'desc' : 'asc';
    dispatchSortAction(key, nextSortOrder);
  };

  React.useEffect(() => {
    if (sortMode !== 'client' || isSimTemplateConfiguration) return;
    if (!resolvedClientSortBy || !resolvedClientSortOrder) return;

    const source = Array.isArray(simOFPs) ? simOFPs : [];
    if (source.length <= 1) return;

    const sortedList = sortWithCreateTimeTieBreaker([...(source || [])], resolvedClientSortBy, resolvedClientSortOrder);
    const next = sortedList.map((item: any) => ({ ...item }));

    if (isSameOrder(source, next)) return;
    dispatch(setSimFlightPlans(next));
  }, [sortMode, isSimTemplateConfiguration, resolvedClientSortBy, resolvedClientSortOrder, simOFPs, dispatch]);

  const sortable = (key: SortBy) => {
    const isActive = activeSortBy === key;

    return {
      scope: 'col' as const,
      className: `clickable ${isActive ? 'sorted-active' : ''}`,
      style: { fontSize: '1rem' as const },
      onClick: () => handleSortClick?.(key),
    };
  };

  const sortIconStyle = (key: SortBy): React.CSSProperties => {
    const isActive = activeSortBy === key;

    if (!isActive) {
      return { marginLeft: 6, opacity: 0.35, display: 'inline-block' };
    }

    return {
      marginLeft: 6,
      opacity: 1,
      display: 'inline-block',
      transform: activeSortOrder === 'asc' ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 120ms ease',
    };
  };

  return (
    <CTable borderless hover>
      <CTableHead>
        <CTableRow>
          <CTableHeaderCell {...sortable('std')}>
            STD
            <span style={sortIconStyle('std')}>
              <DownArrowIcon height={12} width={12} />
            </span>
          </CTableHeaderCell>

          <CTableHeaderCell {...sortable('aircraftType')}>
            Aircraft
            <span style={sortIconStyle('aircraftType')}>
              <DownArrowIcon height={12} width={12} />
            </span>
          </CTableHeaderCell>

          <CTableHeaderCell {...sortable('aircraftReg')}>
            Reg
            <span style={sortIconStyle('aircraftReg')}>
              <DownArrowIcon height={12} width={12} />
            </span>
          </CTableHeaderCell>

          {isSimTemplateConfiguration && (
            <CTableHeaderCell style={{ fontSize: '1rem' }} scope="col">
              Tag
            </CTableHeaderCell>
          )}

          <CTableHeaderCell {...sortable('ufi')}>
            SIM OFP UFI
            <span style={sortIconStyle('ufi')}>
              <DownArrowIcon height={12} width={12} />
            </span>
          </CTableHeaderCell>

          {!isSimTemplateConfiguration && (
            <CTableHeaderCell {...sortable('status')}>
              Status
              <span style={sortIconStyle('status')}>
                <DownArrowIcon height={12} width={12} />
              </span>
            </CTableHeaderCell>
          )}

          <CTableHeaderCell style={{ fontSize: '1rem' }} scope="col">
            Created by
          </CTableHeaderCell>

          <Access route={routePath} access={[AccessType.DELETE]}>
            {isSimTemplateConfiguration && (
              <CTableHeaderCell style={{ fontSize: '1rem' }} scope="col">
                Action
              </CTableHeaderCell>
            )}
          </Access>

          <CTableHeaderCell style={{ fontSize: '1rem' }} scope="col"></CTableHeaderCell>
        </CTableRow>
      </CTableHead>

      <CTableBody>
        {!!simOFPs && simOFPs.length !== 0 ? (
          isSimTemplateConfiguration ? (
            simOFPs.map((simOFP, index) => (
              <SimOFPTemplateTableItem routePath={routePath} key={(simOFP as any)._id || simOFP.ufi} onMutateDone={onMutateDone} {...simOFP} />
            ))
          ) : (
            simOFPs.map((simOFP, index) => <SimOFPTableItem key={`SimOFPTableItem${index}`} {...simOFP} />)
          )
        ) : (
          <CTableRow>
            <CTableDataCell
              align={'middle'}
              colSpan={isSimTemplateConfiguration ? 7 : 6}
              className={'CathaySansRg text-center'}
              style={{ fontSize: '25px', height: '200px', fontWeight: 'bold' }}
            >
              There is no Sim {isSimTemplateConfiguration ? 'Template' : 'Flight plan'}.
            </CTableDataCell>
          </CTableRow>
        )}
      </CTableBody>
    </CTable>
  );
};

export default SimOFPTable;
